import { useState, useEffect } from 'react';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Eye,
  MessageSquare,
  Target,
  Lock,
  FileText,
  Bot,
  Shield,
  ArrowRight,
  CheckCircle,
  BookOpen
} from 'lucide-react';
import { cn } from '../../lib/utils';

const WELCOME_STORAGE_KEY = 'fedbd_welcome_tour_completed';

// Role-specific welcome content
const WELCOME_CONTENT = {
  BID_MANAGER: {
    title: "Welcome to FedBD.ai!",
    subtitle: "You've been invited as a Bid Manager",
    description: "You have access to create and manage pursuits for this organization. Here's what you can do:",
    capabilities: [
      { icon: Target, text: "Create and edit pursuits", allowed: true },
      { icon: Bot, text: "Run AI analysis on RFPs", allowed: true },
      { icon: FileText, text: "Draft proposal sections", allowed: true },
      { icon: BookOpen, text: "Access Knowledge Base", allowed: true },
      { icon: Shield, text: "Track compliance (51% rule)", allowed: true },
    ],
    limitations: [
      { icon: Lock, text: "Cannot manage team members or billing" },
    ],
    color: "blue"
  },
  REVIEWER: {
    title: "Welcome to FedBD.ai!",
    subtitle: "You've been invited as a Reviewer",
    description: "Your role is to provide expert feedback on specific pursuits. Here's what you can do:",
    capabilities: [
      { icon: Eye, text: "View assigned pursuits only", allowed: true },
      { icon: MessageSquare, text: "Add comments and annotations", allowed: true },
      { icon: Target, text: "Score Go/No-Go evaluations", allowed: true },
    ],
    limitations: [
      { icon: Lock, text: "Cannot create or edit pursuits" },
      { icon: Lock, text: "Cannot access Knowledge Base" },
      { icon: Lock, text: "Cannot see other pursuits" },
    ],
    color: "purple"
  }
};

export default function WelcomeTourModal() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Check if user is a guest and hasn't seen the welcome tour
    if (user && (user.role === ROLES.BID_MANAGER || user.role === ROLES.REVIEWER)) {
      const completedTours = JSON.parse(localStorage.getItem(WELCOME_STORAGE_KEY) || '{}');
      if (!completedTours[user.id]) {
        // Delay to let the main UI render first
        setTimeout(() => setIsOpen(true), 1000);
      }
    }
  }, [user]);

  const handleComplete = () => {
    if (user) {
      const completedTours = JSON.parse(localStorage.getItem(WELCOME_STORAGE_KEY) || '{}');
      completedTours[user.id] = true;
      localStorage.setItem(WELCOME_STORAGE_KEY, JSON.stringify(completedTours));
    }
    setIsOpen(false);
  };

  const content = WELCOME_CONTENT[user?.role];
  if (!content) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="welcome-tour-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={cn(
              content.color === "blue" && "bg-blue-100 text-blue-700",
              content.color === "purple" && "bg-purple-100 text-purple-700"
            )}>
              {user?.role?.replace('_', ' ')}
            </Badge>
          </div>
          <DialogTitle className="text-xl">{content.title}</DialogTitle>
          <DialogDescription className="text-base font-medium text-slate-700">
            {content.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-slate-600 mb-4">{content.description}</p>
          
          {/* Capabilities */}
          <div className="space-y-2 mb-4">
            {content.capabilities.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div key={idx} className="flex items-center gap-3 p-2 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <Icon className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-700">{cap.text}</span>
                </div>
              );
            })}
          </div>

          {/* Limitations */}
          {content.limitations.length > 0 && (
            <>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Limitations</p>
              <div className="space-y-2">
                {content.limitations.map((lim, idx) => {
                  const Icon = lim.icon;
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-100 rounded-lg">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{lim.text}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Security Note */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <strong>Security Note:</strong> Your access is scoped to protect sensitive data. 
              You only see what's relevant to your role. This is by design for compliance.
            </p>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.location.href = '/help'}
            className="text-slate-600"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            View Help Center
          </Button>
          <Button onClick={handleComplete} data-testid="welcome-tour-complete">
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
